import LegalShell from '../../components/legal/LegalShell';
import { CONSENT_VERSIONS } from '../../auth/useConsent';

export default function AiConsent() {
  return (
    <LegalShell
      title="AI Consent Notice"
      version={CONSENT_VERSIONS.ai}
      effective="2026-07-25"
    >
      <p>
        This AI Consent Notice describes the artificial-intelligence
        capabilities of the OculusMind.AI website and AI-agent platform
        (collectively, the "Services"), the third-party AI providers we route
        your data to, and the choices you have. It is issued by Andrew Paris
        LLC, d/b/a MediaShare-Link ("Company," "we," "us," "our"),
        and is provided in addition to — not in place of — our Privacy Policy
        and Terms of Service. It governs consent to AI processing only; our use
        of cookies and similar tracking technologies, and your choices about
        them, are governed separately by our Cookie Policy and the cookie-consent
        controls described there.
      </p>
      <p>
        By creating an account or otherwise using the AI features of the
        Services, you acknowledge this notice and consent to the AI processing
        described below. You may withdraw your consent at any time as
        described in "Withdrawing Your Consent" near the end of this notice.
      </p>

      <h2>What This Notice Covers</h2>
      <p>The Services use generative-AI models and AI agents to:</p>
      <ul>
        <li>Hold conversations with you and respond to your prompts.</li>
        <li>
          Take instructions on your behalf, including reasoning over
          multi-step problems and orchestrating tools you have authorized.
        </li>
        <li>
          Retrieve information from external websites, search engines, and
          third-party application programming interfaces ("APIs") in order to
          answer your questions or perform tasks you have requested.
        </li>
        <li>
          Process documents, images, audio, and other files you upload,
          including extracting text, summarizing content, and producing derived
          artifacts.
        </li>
        <li>Generate text and structured data based on your inputs.</li>
        <li>
          Apply automated content-safety filters to detect prompts and outputs
          that violate our policies.
        </li>
      </ul>
      <p>
        This notice is the "consent gate" required before any of the AI
        capabilities above will process your data. It does not describe
        non-AI uses of your information — those are addressed in our Privacy
        Policy.
      </p>

      <h2>AI Providers We Route Your Data To</h2>
      <p>
        To deliver AI features, portions of your inputs — including chat
        messages, prompts, instructions, attached files, and any conversational
        context needed to interpret them — are transmitted to the AI model
        providers and supporting services listed below. Each provider receives
        only the data necessary to perform its function and is contractually
        obligated to protect your data with safeguards at least as protective
        as those described in our Privacy Policy.
      </p>
      <ul>
        <li>
          <strong>Anthropic, PBC</strong> ("Claude" language and vision models,
          accessed through AWS Bedrock): receives your chat messages, prompts,
          attachments, and conversation context in order to generate AI
          responses and perform automated content moderation. When Claude is
          accessed through AWS Bedrock, Anthropic does not use your inputs or
          outputs to train its models.
        </li>
        <li>
          <strong>AWS Bedrock</strong> (Amazon Web Services, Inc.): hosts the
          Anthropic Claude models and any additional generative-AI models we
          may enable over time. AWS Bedrock processes data within the AWS
          region in which the Services are provisioned (United States).
        </li>
        <li>
          <strong>AWS Cognito</strong> (Amazon Web Services, Inc.): receives
          your email address, password, and multi-factor authentication state
          in order to authenticate you and manage your account identity.
        </li>
        <li>
          <strong>AWS DynamoDB</strong> (Amazon Web Services, Inc.): stores
          account records, agent configuration, conversation metadata,
          message indices, and other structured data required to operate the
          Services and to make your data retrievable.
        </li>
        <li>
          <strong>AWS S3</strong> (Amazon Web Services, Inc.): stores your
          conversations, agent outputs, and uploaded files in accordance with
          the retention practices described in our Privacy Policy.
        </li>
        <li>
          <strong>AWS SES</strong> (Amazon Web Services, Inc.): receives your
          email address in order to send verification messages, security
          notifications, and other account-related communications.
        </li>
        <li>
          <strong>AWS CloudWatch</strong> (Amazon Web Services, Inc.):
          receives operational logs, metrics, and diagnostic events generated
          by the Services in order to monitor system health, investigate
          errors, and detect abuse. These logs may incidentally contain
          technical identifiers (such as request IDs and IP addresses),
          portions of error messages, and metadata describing your requests;
          we do not write the free-text content of your prompts or AI
          outputs into CloudWatch logs in the ordinary course.
        </li>
        <li>
          <strong>AWS X-Ray</strong> (Amazon Web Services, Inc.): receives
          distributed traces of requests as they flow through the Services
          in order to diagnose performance problems and isolate failures
          across components. Trace data includes request timing, service
          identifiers, and metadata about each step in a request's path, but
          is not used to capture the full content of your prompts or AI
          outputs.
        </li>
      </ul>
      <p>
        If we add additional AI model providers or supporting services, we
        will update this notice and, where the change is material, prompt you
        to re-consent before they receive your data.
      </p>

      <h2>External Websites and Agent-Initiated Research</h2>
      <p>
        A central capability of the Services is the ability of our AI agents
        to gather information from external websites, search engines,
        knowledge bases, and third-party APIs on your behalf. This capability
        operates differently from the AI providers listed above and warrants
        its own disclosure.
      </p>
      <p>
        When you instruct an agent to research a topic, look something up,
        fetch a page, query a public dataset, or otherwise retrieve
        information from the public internet:
      </p>
      <ul>
        <li>
          Portions of your inputs — including the text of your query,
          paraphrased or reformulated versions of your request, follow-up
          refinements, and any parameters needed to perform the lookup —
          will be transmitted to the relevant external website, search
          engine, or API as a normal side-effect of carrying out the task you
          requested.
        </li>
        <li>
          Those external services may log the request, may associate it with
          the IP address or other technical identifiers their systems
          receive, and may use the request in accordance with their own terms
          and privacy policies. We do not control how they handle the data
          they receive.
        </li>
        <li>
          Unlike the AI providers listed above, external research targets are
          not under AI-specific data-handling agreements with us. By using
          agent capabilities that perform external research, you accept that
          your queries may reach third parties whose data practices are
          governed solely by their own policies.
        </li>
      </ul>
      <p>
        If you do not want a particular piece of information transmitted to
        external services, do not include it in instructions given to an
        agent that performs web search, retrieval, or external-API access.
      </p>

      <h2>Accuracy and Hallucinations</h2>
      <p>
        AI-generated output can be incomplete, out of date, or wrong. Large
        language models — including the models that power the Services —
        sometimes produce content that sounds confident but is factually
        incorrect, fabricated ("hallucinated"), or based on stale information.
        Agent-initiated research can mitigate but does not eliminate this
        risk; agents can misinterpret search results, miss relevant sources,
        or summarize material inaccurately.
      </p>
      <p>
        You should treat AI output as a starting point, not a final answer.
        Do not rely on the Services for advice that requires professional
        judgment, including medical, legal, financial, tax, mental-health, or
        safety-critical decisions. Always verify important information
        against authoritative sources before acting on it.
      </p>

      <h2>Content Moderation and Safety</h2>
      <p>
        We apply automated safety filters to prompts and outputs in order to
        detect content that violates our policies, including (without
        limitation) content depicting minors, content promoting violence,
        content that facilitates illegal activity, and content that targets
        individuals for harassment. Some filters run within the Services;
        others run within an AI provider's infrastructure. Detection systems
        are imperfect — they may flag benign content or allow prohibited
        content through — and your continued use of the Services constitutes
        acknowledgment of that limitation. Material that our filters or
        operational personnel identify as a serious policy or safety
        violation may be retained for investigation and reported to
        law-enforcement authorities where required by law.
      </p>

      <h2>Data Retention</h2>
      <p>
        Data transmitted to AI providers in order to generate a response is
        retained on our systems in accordance with the retention practices
        described in our Privacy Policy. Conversation history, prompts, and
        uploaded files are retained while your account is active and may be
        expired automatically after a period of inactivity. Once data has
        been transmitted to a third-party AI provider or to an external
        research target, its further retention is governed by that party's
        own policies, not by ours.
      </p>

      <h2>Withdrawing Your Consent</h2>
      <p>
        You may withdraw your consent to AI processing at any time by opening
        Settings within the Services and selecting "Withdraw AI Consent."
        Because the core features of the Services depend on the AI
        processing described above, withdrawing consent will sign you out
        and disable AI features until you sign back in and re-consent.
      </p>
      <p>
        Withdrawing consent does not retroactively delete data already
        transmitted to AI providers, data already received by external
        websites or APIs that an agent contacted on your behalf, or data
        already stored in your account. To remove account data, see the
        "Your Rights and Choices" section of our Privacy Policy.
      </p>

      <h2>Eligibility</h2>
      <p>
        The Services, including the AI features described in this notice,
        are available only to individuals who are eighteen (18) years of age
        or older. We do not knowingly provide AI features to anyone under
        eighteen.
      </p>

      <h2>Changes to This Notice</h2>
      <p>
        We may update this AI Consent Notice from time to time. Any changes
        will be reflected by updating the "Last updated" date at the top of
        this document. Where a change is material — for example, the
        addition of a new category of AI provider — we will re-prompt you to
        consent before the change takes effect for your account.
      </p>

      <h2>Contact</h2>
      <address>
        Andrew Paris LLC, d/b/a MediaShare-Link<br />
        5900 Balcones Dr Suite 21007<br />
        Austin, TX 78731<br />
        United States
      </address>
      <p>
        Email: <a href="mailto:Help@OculusMind.AI">Help@OculusMind.AI</a>
      </p>
    </LegalShell>
  );
}
